import React, { useState, useEffect } from 'react';
import axios from 'axios';
import '../styles/About.css';

const EditorialTeam = () => {
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTeamMembers();
  }, []);

  const fetchTeamMembers = async () => {
    try {
      // Simulated API call - replace with actual endpoint
      const response = await axios.get('/api/editorial-team');
      // Ensure response.data is an array
      if (Array.isArray(response.data)) {
        setTeamMembers(response.data);
      } else {
        throw new Error('Invalid data format');
      }
    } catch (err) {
      console.error('Error fetching editorial team:', err);
      // Use placeholder data if API fails
      setTeamMembers([
        {
          id: 1,
          name: "Prof. Abdullahi Bala Usman",
          position: "Editor-in-Chief",
          department: "Agricultural Economics and Extension",
          institution: "Federal University Wukari",
          email: "editor@fuwukari.edu.ng",
          specialization: "Agricultural Economics, Rural Development"
        },
        {
          id: 2,
          name: "Dr. Fatima Abubakar Mohammed",
          position: "Managing Editor",
          department: "Crop Science",
          institution: "Federal University Wukari", 
          email: "managing.editor@fuwukari.edu.ng",
          specialization: "Crop Production, Plant Breeding"
        },
        {
          id: 3,
          name: "Prof. Emmanuel Nkwemeka Okoro",
          position: "Associate Editor",
          department: "Animal Science",
          institution: "University of Nigeria, Nsukka",
          email: "e.okoro@unn.edu.ng",
          specialization: "Animal Nutrition, Livestock Production"
        },
        {
          id: 4,
          name: "Dr. Amina Musa Hassan",
          position: "Section Editor - Plant Sciences",
          department: "Plant Biology",
          institution: "Ahmadu Bello University",
          email: "a.hassan@abu.edu.ng",
          specialization: "Plant Pathology, Plant Genetics"
        },
        {
          id: 5,
          name: "Prof. Bartholomew Kachalla Mohammed",
          position: "Section Editor - Soil Science",
          department: "Soil Science",
          institution: "Bayero University Kano",
          email: "b.mohammed@buk.edu.ng",
          specialization: "Soil Fertility, Land Management"
        },
        {
          id: 6,
          name: "Dr. Priscilla Chioma Nwosu",
          position: "Section Editor - Environmental Sciences",
          department: "Environmental Sciences",
          institution: "University of Port Harcourt",
          email: "p.nwosu@uniport.edu.ng",
          specialization: "Environmental Impact Assessment, Climate Change"
        },
        {
          id: 7,
          name: "Dr. Rasheed Tunde Aliyu",
          position: "Section Editor - Agricultural Engineering",
          department: "Agricultural Engineering",
          institution: "Federal University of Technology, Minna",
          email: "r.aliyu@futminna.edu.ng",
          specialization: "Farm Machinery, Post-harvest Technology"
        },
        {
          id: 8,
          name: "Prof. Sarah Adunni Yakubu",
          position: "Section Editor - Food Science",
          department: "Food Science and Technology",
          institution: "Federal University Wukari",
          email: "s.yakubu@fuwukari.edu.ng",
          specialization: "Food Processing, Food Safety"
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="container py-4">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  const editorInChief = teamMembers.find(member => member.position === "Editor-in-Chief");
  const managingEditor = teamMembers.find(member => member.position === "Managing Editor");
  const associateEditors = teamMembers.filter(member => member.position === "Associate Editor");
  const sectionEditors = teamMembers.filter(member => member.position && member.position.includes("Section Editor"));

  return (
    <>
      {/* Hero Section */}
      <div className="about-hero">
        <div className="container">
          <h1 className="about-hero-title">
            <i className="fas fa-users me-3"></i>
            Editorial Team
          </h1>
          <p className="about-hero-subtitle">
            Distinguished scholars guiding academic excellence
          </p>
        </div>
      </div>

      <div className="container py-5">
        <div className="row">
          <div className="col-lg-10 mx-auto">
            <div className="about-card" style={{ animationDelay: '0.1s' }}>
              <p className="text-center lead">
                The FUWJALS Editorial Team comprises distinguished scholars and researchers
                committed to maintaining the highest standards of academic publishing in
                agriculture and life sciences.
              </p>
            </div>

            {/* Editor-in-Chief */}
            {editorInChief && (
              <div className="about-card" style={{ animationDelay: '0.2s' }}>
                <div className="about-card-header">
                  <h2 className="about-card-title">
                    <i className="fas fa-crown"></i>
                    Editor-in-Chief
                  </h2>
                </div>
                <div className="card-body">
                  <div className="row align-items-center">
                    <div className="col-md-2 text-center">
                      <div className="bg-light rounded-circle mx-auto mb-3" style={{width: '80px', height: '80px', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                        <i className="fas fa-user fa-2x text-muted"></i>
                      </div>
                    </div>
                    <div className="col-md-10">
                      <h4 className="card-title">{editorInChief.name}</h4>
                      <p className="text-primary mb-2">{editorInChief.position}</p>
                      <p className="mb-1"><strong>Department:</strong> {editorInChief.department}</p>
                      <p className="mb-1"><strong>Institution:</strong> {editorInChief.institution}</p>
                      <p className="mb-1"><strong>Specialization:</strong> {editorInChief.specialization}</p>
                      <p className="mb-0"><strong>Email:</strong> <a href={`mailto:${editorInChief.email}`}>{editorInChief.email}</a></p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Managing Editor */}
            {managingEditor && (
              <div className="about-card" style={{ animationDelay: '0.3s' }}>
                <div className="about-card-header">
                  <h2 className="about-card-title">
                    <i className="fas fa-edit"></i>
                    Managing Editor
                  </h2>
                </div>
                <div className="card-body">
                  <div className="row align-items-center">
                    <div className="col-md-2 text-center">
                      <div className="bg-light rounded-circle mx-auto mb-3" style={{width: '80px', height: '80px', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                        <i className="fas fa-user fa-2x text-muted"></i>
                      </div>
                    </div>
                    <div className="col-md-10">
                      <h4 className="card-title">{managingEditor.name}</h4>
                      <p className="text-primary mb-2">{managingEditor.position}</p>
                      <p className="mb-1"><strong>Department:</strong> {managingEditor.department}</p>
                      <p className="mb-1"><strong>Institution:</strong> {managingEditor.institution}</p>
                      <p className="mb-1"><strong>Specialization:</strong> {managingEditor.specialization}</p>
                      <p className="mb-0"><strong>Email:</strong> <a href={`mailto:${managingEditor.email}`}>{managingEditor.email}</a></p>
                    </div>
                  </div>
                </div>
              </div>
            )}

          {/* Associate Editors */}
          {associateEditors.length > 0 && (
            <div className="mb-5">
              <h2 className="mb-4">Associate Editors</h2>
              <div className="row">
                {associateEditors.map(editor => (
                  <div key={editor.id} className="col-lg-6 mb-4">
                    <div className="card h-100">
                      <div className="card-body">
                        <div className="row align-items-center">
                          <div className="col-3 text-center">
                            <div className="bg-light rounded-circle mx-auto mb-2" style={{width: '60px', height: '60px', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                              <i className="fas fa-user fa-lg text-muted"></i>
                            </div>
                          </div>
                          <div className="col-9">
                            <h5 className="card-title">{editor.name}</h5>
                            <p className="text-primary small mb-1">{editor.position}</p>
                            <p className="small mb-1">{editor.department}</p>
                            <p className="small mb-1">{editor.institution}</p>
                            <p className="small mb-1"><strong>Specialization:</strong> {editor.specialization}</p>
                            <p className="small mb-0"><a href={`mailto:${editor.email}`}>{editor.email}</a></p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Section Editors */}
          {sectionEditors.length > 0 && (
            <div className="mb-5">
              <h2 className="mb-4">Section Editors</h2>
              <div className="row">
                {sectionEditors.map(editor => (
                  <div key={editor.id} className="col-lg-6 mb-4">
                    <div className="card h-100">
                      <div className="card-body">
                        <div className="row align-items-center">
                          <div className="col-3 text-center">
                            <div className="bg-light rounded-circle mx-auto mb-2" style={{width: '60px', height: '60px', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                              <i className="fas fa-user fa-lg text-muted"></i>
                            </div>
                          </div>
                          <div className="col-9">
                            <h5 className="card-title">{editor.name}</h5>
                            <p className="text-primary small mb-1">{editor.position}</p>
                            <p className="small mb-1">{editor.department}</p>
                            <p className="small mb-1">{editor.institution}</p>
                            <p className="small mb-1"><strong>Specialization:</strong> {editor.specialization}</p>
                            <p className="small mb-0"><a href={`mailto:${editor.email}`}>{editor.email}</a></p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="card mb-5">
            <div className="card-body">
              <h3 className="card-title">Editorial Responsibilities</h3>
              <div className="row">
                <div className="col-md-6">
                  <h5>Editor-in-Chief</h5>
                  <ul>
                    <li>Overall journal management</li>
                    <li>Final editorial decisions</li>
                    <li>Strategic planning and vision</li>
                    <li>Quality assurance</li>
                  </ul>
                </div>
                <div className="col-md-6">
                  <h5>Managing Editor</h5>
                  <ul>
                    <li>Day-to-day operations</li>
                    <li>Manuscript tracking</li>
                    <li>Communication with authors</li>
                    <li>Production coordination</li>
                  </ul>
                </div>
                <div className="col-md-6">
                  <h5>Associate Editors</h5>
                  <ul>
                    <li>Manuscript evaluation</li>
                    <li>Reviewer assignment</li>
                    <li>Editorial recommendations</li>
                    <li>Special issue coordination</li>
                  </ul>
                </div>
                <div className="col-md-6">
                  <h5>Section Editors</h5>
                  <ul>
                    <li>Subject area expertise</li>
                    <li>Specialized reviews</li>
                    <li>Field-specific standards</li>
                    <li>Reviewer networks</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

            <div className="text-center">
              <a href="/contact" className="btn btn-primary">Contact Editorial Office</a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default EditorialTeam;